import axios from 'axios'
import { ref, watch } from 'vue'

import router from '@/router'
import { apiService, type FailedEvent } from '@/services/apiService'
import { resolveApiResourceUrl } from '@/services/apiUrlService'
import CONFIG from '@/services/configService'
import { connectivityService } from '@/services/connectivityService'
import { showConfirm } from '@/services/dialogService'
import {
  collectDependentEventIds,
  createdClientIds,
  referencedClientIds,
} from '@/services/eventDependencyService'
import { formatByteSize } from '@/services/formattingService'
import { generateId } from '@/services/idService'
import { dbService, type EventQueueOwnerContext } from '@/services/indexedDBService'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import { poiSnapshot } from '@/services/poiService'
import { leaveGuardCopy, syncToasts } from '@/services/syncCopy'
import { syncDialogVisible } from '@/services/syncUiState'
import { showError } from '@/services/toastService'
import { applyPersistedWorkspaceTourVisibility } from '@/services/tourVisibilityLocalStorageService'
import { useAuthStore } from '@/stores/authStore'
import { useEventStore } from '@/stores/eventStore'
import { useOfflineStore } from '@/stores/offlineStore'
import { usePhotoStore } from '@/stores/photoStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { EventType } from '@/types/event'
import type { Event } from '@/types/event'
import type { Photo } from '@/types/photo'
import type { WorkspacePOI } from '@/types/poi'
import type { Tour } from '@/types/tour'
import type { WorkspaceDetailPayload } from '@/types/workspaceDetail'
import { hydrateApiSegments } from '@/utils/hydrateApiSegments'
import { requireTrackClientId } from '@/utils/trackRoutePoints'

import type { RouteLocationNormalized } from 'vue-router'

// Custom error classes for better error categorization
class NonRetryableError extends Error {
  constructor(
    message: string,
    public readonly statusCode?: number,
  ) {
    super(message)
    this.name = 'NonRetryableError'
  }
}

class AuthenticationError extends NonRetryableError {
  constructor(message: string, statusCode: number) {
    super(message, statusCode)
    this.name = 'AuthenticationError'
  }
}

/**
 * The server rejected the batch's content (4xx validation) — retrying the identical payload
 * can never succeed, so the rejected events are quarantined instead of retried forever.
 */
class ValidationError extends NonRetryableError {
  constructor(
    message: string,
    statusCode: number,
    // Server-named culprits (backend ask #1). Present for per-event rejections; absent when the
    // whole request failed with no per-event detail (then the whole batch is quarantined).
    public readonly failedEvents?: FailedEvent[],
  ) {
    super(message, statusCode)
    this.name = 'ValidationError'
  }
}

class EventQueueOwnerChangedError extends NonRetryableError {
  constructor() {
    super('Event queue account changed')
    this.name = 'EventQueueOwnerChangedError'
  }
}

// Only true content rejections belong here — retrying the identical payload can never succeed,
// so these events are quarantined instead of retried forever. 404 (wrong endpoint / API base /
// a prerequisite that hasn't synced yet) and 413 (proxy or body-size limit) are transient or
// infrastructural: they must stay retryable so valid offline work is never quarantined for a
// misconfiguration or a momentary limit.
const VALIDATION_STATUS_CODES = new Set([400, 409, 422])

type WorkspaceDetailResponse = WorkspaceDetailPayload

type PopulateStoresOptions = {
  detailsLoaded: boolean
  preserveCurrentState?: boolean
}

// Result of processing one batch (Option C partial success): the events the server committed
// (removed from the queue) and how many it permanently rejected (quarantined + dependents).
interface BatchOutcome {
  committed: Event[]
  quarantinedCount: number
}

interface SyncOwnerContext {
  epoch: number
  ownerUserId: number
  eventOwner: EventQueueOwnerContext
  signal: AbortSignal
}

// Helper function to check if sync error notifications should be suppressed
const shouldSuppressSyncErrorNotifications = (): boolean => {
  try {
    const authStore = useAuthStore()

    // Suppress sync error notifications for unauthenticated users
    return !authStore.isAuthenticated
  } catch (error) {
    // If we can't access the stores, don't suppress but log for diagnostics
    logService.warn(LOG_CATEGORIES.SYNC, 'shouldSuppressSyncErrorNotifications failed', {
      error,
    })
    return false
  }
}

/**
 * Filter out properties that should not be sent to the server:
 * - Properties that start with an underscore (private/local-only)
 * - Properties that are File objects (should be sent separately)
 */
function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return Object.prototype.toString.call(value) === '[object Object]'
}

function normalizeSyncValue(key: string, value: unknown): unknown {
  if (key === 'gps_accuracy' && typeof value === 'number') {
    return Number.isFinite(value) ? Math.round(value) : undefined
  }

  if (Array.isArray(value)) {
    return value.map((item) => normalizeSyncValue('', item))
  }

  if (!isPlainRecord(value)) {
    return value
  }

  return Object.fromEntries(
    Object.entries(value)
      .filter(
        ([entryKey, entryValue]) =>
          !entryKey.startsWith('_') &&
          !(entryValue instanceof File) &&
          !(entryValue instanceof Blob),
      )
      .map(([entryKey, entryValue]) => [entryKey, normalizeSyncValue(entryKey, entryValue)]),
  )
}

function getRelevantProperties<T extends Record<string, unknown>>(obj: T): Partial<T> {
  return normalizeSyncValue('', obj) as Partial<T>
}

function isNetworkOfflineError(error: unknown): boolean {
  if (!connectivityService.isEffectivelyOnline.value) {
    return true
  }

  if (!axios.isAxiosError(error)) {
    return false
  }

  if (!error.response) {
    return true
  }

  return false
}

class SyncService {
  private isInitialized: boolean = false
  private syncTimeoutId: number | null = null
  private syncSuppressionDepth: number = 0
  private syncRequestedDuringSuppression: boolean = false
  private leaveProtectionInitialized: boolean = false
  private workspaceLoadEpoch: number = 0
  private eventQueueEpoch: number = 0
  private activeSyncAbortController: AbortController | null = null

  // Reactive sync status
  public readonly syncStatus = ref<'idle' | 'syncing' | 'success' | 'error' | 'offline'>('idle')
  public readonly lastErrorMessage = ref<string | null>(null)
  public readonly forceOfflineMode = connectivityService.forceOfflineMode

  // Retry logic state
  private retryCount: number = 0
  private readonly maxRetries: number = 5
  private readonly baseRetryInterval: number = 2000 // 2 seconds
  private readonly exponentialBase: number = 2
  private readonly cooldownPeriod: number = 60000 // 60 seconds
  private retryTimeoutId: number | null = null
  private cooldownTimeoutId: number | null = null
  private isInCooldown: boolean = false

  // Reactive retry state for ambient indicators (sync-feedback.md §4.4): which attempt is
  // scheduled and when it fires. `null` timestamps mean no retry/cooldown is pending.
  public readonly retryAttempt = ref(0)
  public readonly retryMaxAttempts = this.maxRetries
  public readonly nextRetryAt = ref<number | null>(null)
  public readonly cooldownUntil = ref<number | null>(null)

  // How many items the current drain episode started with (grows if more events are queued
  // mid-drain, null when the queue is empty). Together with the live queue length this gives
  // the user real progress: "synced X of Y" (sync-feedback.md §4.2).
  public readonly drainTotal = ref<number | null>(null)

  // The batch currently in flight: item count and byte-level upload fraction (0..1). Lets the
  // progress bar move WITHIN a long batch — a single large upload would otherwise sit at 0%
  // until the server responds. Fraction stays at 1 while the server processes the request;
  // `currentBatchUploadedAt` marks when that processing phase began (drives the UI's
  // asymptotic progress creep, since server-side processing time is unmeasurable).
  public readonly currentBatchCount = ref(0)
  public readonly currentBatchUploadFraction = ref(0)
  public readonly currentBatchUploadedAt = ref<number | null>(null)
  // Event clientIds of the in-flight batch — drives per-item "syncing…" status in the
  // activity view (sync-feedback.md §5).
  public readonly currentBatchClientIds = ref<ReadonlySet<string>>(new Set())

  // Honesty about failure (sync-feedback.md §6, pre-quarantine): the events of the last
  // FAILED batch (marks the affected items "can't sync yet"), and whether a full retry
  // cycle has already failed — at that point "will retry automatically" stops being a
  // credible promise and the UI switches to actionable copy.
  public readonly lastFailedBatchClientIds = ref<ReadonlySet<string>>(new Set())
  public readonly persistentFailure = ref(false)

  // Set by SyncStatusDialog while it is visible: the dialog already shows every state in
  // place, so ambient toasts would be double feedback and are suppressed meanwhile.
  // (Backed by the shared syncUiState module so stores can read it without import cycles.)
  public readonly syncDialogVisible = syncDialogVisible

  // One toast per entry into an actionable error state; ambient indicators carry it afterwards.
  private attentionToastShown = false
  private readonly beforeUnloadHandler = (event: BeforeUnloadEvent) => {
    // Browser reload/close prompts are only truthful while a batch is actively in flight. Idle
    // queued work is durable in IndexedDB and will resume next launch; warning there trains users
    // to ignore the one moment where closing can interrupt an upload.
    if (this.currentBatchCount.value === 0) {
      return
    }

    event.preventDefault()
    event.returnValue = ''
  }

  /**
   * Setup automatic synchronization process
   */
  setupAutomaticSync() {
    if (this.isInitialized) return
    this.isInitialized = true
    this.setupLeaveProtection()

    // Set initial status based on current online state
    if (!connectivityService.isEffectivelyOnline.value) {
      this.syncStatus.value = 'offline'
    }

    // Watch for when the device comes back online
    watch(connectivityService.isEffectivelyOnline, (online, wasOnline) => {
      if (online) {
        // When we come back online (transition from offline to online)
        if (wasOnline === false) {
          this.handleOnlineStatusChange()
        }
      } else {
        // When we go offline, set status to offline
        // Only if we're not currently syncing (preserve syncing state)
        if (this.syncStatus.value !== 'syncing') {
          this.syncStatus.value = 'offline'
          this.lastErrorMessage.value = 'Device is offline'
        }

        // Clear any pending retry timeouts when going offline
        this.clearRetryTimeouts()
      }
    })

    // Listen to the event store to automatically sync when new events arrive
    // This complements the existing scheduleSync() call in the event store
    const eventStore = useEventStore()

    // Watch for changes in unsynced events count to trigger immediate sync
    // This handles the case: "if sync was successful and there are no new events, sync next time an event was added"
    let lastUnsyncedCount = 0
    watch(
      () => eventStore.unsyncedEvents.length,
      (newCount) => {
        // If events were added and we're online and not in cooldown
        if (
          newCount > lastUnsyncedCount &&
          connectivityService.isEffectivelyOnline.value &&
          !this.isInCooldown
        ) {
          this.triggerImmediateSync()
        }
        lastUnsyncedCount = newCount
      },
    )

    // If events were already loaded from IndexedDB before the watcher was set up (e.g. on hard refresh
    // with unsynced events from a previous session), the watcher won't fire for the initial value.
    // Schedule a sync now so those events are not silently skipped.
    if (eventStore.unsyncedEvents.length > 0) {
      this.scheduleSync()
    }
  }

  private resetErrorStateForNewSyncAttempt(): void {
    if (this.syncStatus.value !== 'error') {
      return
    }

    this.syncStatus.value = 'idle'
    this.lastErrorMessage.value = null
    this.attentionToastShown = false
    this.retryCount = 0
  }

  private triggerImmediateSync(): void {
    if (this.syncSuppressionDepth > 0) {
      this.syncRequestedDuringSuppression = true
      return
    }

    if (
      !connectivityService.isEffectivelyOnline.value ||
      this.isInCooldown ||
      !this.hasUnsyncedChanges() ||
      this.retryTimeoutId !== null
    ) {
      return
    }

    if (this.syncStatus.value === 'syncing') {
      return
    }

    this.resetErrorStateForNewSyncAttempt()
    void this.synchronize()
  }

  beginEventBatch(): void {
    this.syncSuppressionDepth++

    if (this.syncTimeoutId !== null) {
      clearTimeout(this.syncTimeoutId)
      this.syncTimeoutId = null
      this.syncRequestedDuringSuppression = true
    }
  }

  endEventBatch(): void {
    if (this.syncSuppressionDepth === 0) {
      logService.warn(
        LOG_CATEGORIES.SYNC,
        'Ignoring unmatched endEventBatch call while no event batch is active',
      )
      return
    }

    this.syncSuppressionDepth--

    if (this.syncSuppressionDepth > 0) {
      return
    }

    const shouldTriggerSync = this.syncRequestedDuringSuppression
    this.syncRequestedDuringSuppression = false

    if (shouldTriggerSync) {
      this.triggerImmediateSync()
    }
  }

  async runWithEventBatch<T>(operation: () => Promise<T>): Promise<T> {
    this.beginEventBatch()

    try {
      return await operation()
    } finally {
      this.endEventBatch()
    }
  }

  private setupLeaveProtection() {
    if (this.leaveProtectionInitialized || typeof window === 'undefined') {
      return
    }

    this.leaveProtectionInitialized = true

    window.addEventListener('beforeunload', this.beforeUnloadHandler)
    router.beforeEach(async (to, from) => {
      const allowed = this.shouldWarnBeforeRouteChange(to, from)
        ? await this.confirmSafeToLeave('Change Tab')
        : true

      if (allowed && to.fullPath !== from.fullPath) {
        this.invalidateWorkspaceLoads()
      }

      return allowed
    })
  }

  public invalidateWorkspaceLoads(): void {
    this.workspaceLoadEpoch += 1
  }

  public invalidateEventQueueOwner(): void {
    this.eventQueueEpoch += 1
    this.activeSyncAbortController?.abort()
    this.activeSyncAbortController = null
    this.resetSyncState()
    this.currentBatchCount.value = 0
    this.currentBatchUploadFraction.value = 0
    this.currentBatchUploadedAt.value = null
    this.currentBatchClientIds.value = new Set()
  }

  private createSyncOwnerContext(ownerUserId: number): SyncOwnerContext {
    const eventOwner = useEventStore().requireOwnerContext()
    if (eventOwner.ownerUserId !== ownerUserId) {
      throw new EventQueueOwnerChangedError()
    }
    const controller = new AbortController()
    this.activeSyncAbortController = controller
    return {
      epoch: this.eventQueueEpoch,
      ownerUserId,
      eventOwner,
      signal: controller.signal,
    }
  }

  private isSyncOwnerCurrent(context: SyncOwnerContext): boolean {
    return (
      context.epoch === this.eventQueueEpoch &&
      !context.signal.aborted &&
      useAuthStore().user?.userId === context.ownerUserId &&
      useEventStore().isOwnerContextCurrent(context.eventOwner)
    )
  }

  private assertSyncOwnerCurrent(context: SyncOwnerContext): void {
    if (!this.isSyncOwnerCurrent(context)) {
      throw new EventQueueOwnerChangedError()
    }
  }

  private beginWorkspaceLoad(): number {
    this.invalidateWorkspaceLoads()
    return this.workspaceLoadEpoch
  }

  private isWorkspaceLoadCurrent(loadEpoch: number, userId: number | null): boolean {
    return loadEpoch === this.workspaceLoadEpoch && (useAuthStore().user?.userId ?? null) === userId
  }

  private shouldWarnBeforeRouteChange(
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
  ): boolean {
    if (!this.hasUnsyncedChanges()) {
      return false
    }

    if (from.matched.length === 0 || to.fullPath === from.fullPath) {
      return false
    }

    const toRootComponent = to.matched[0]?.components?.default
    const fromRootComponent = from.matched[0]?.components?.default

    if (!toRootComponent || !fromRootComponent) {
      return true
    }

    return toRootComponent !== fromRootComponent
  }

  /**
   * Calculate exponential backoff delay for retries
   */
  private calculateRetryDelay(): number {
    return this.baseRetryInterval * Math.pow(this.exponentialBase, this.retryCount)
  }

  /**
   * Reset all retry-related state after successful sync or when coming back online
   */
  private resetRetryState(): void {
    this.retryCount = 0
    this.isInCooldown = false
    this.attentionToastShown = false
    this.persistentFailure.value = false
    this.lastFailedBatchClientIds.value = new Set()
    this.clearRetryTimeouts()
  }

  /**
   * Clear all retry-related timeouts
   */
  private clearRetryTimeouts(): void {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId)
      this.retryTimeoutId = null
    }
    if (this.cooldownTimeoutId) {
      clearTimeout(this.cooldownTimeoutId)
      this.cooldownTimeoutId = null
    }
    this.retryAttempt.value = 0
    this.nextRetryAt.value = null
    this.cooldownUntil.value = null
  }

  /**
   * Determine if an error should be retried
   */
  private isRetryableError(error: unknown): boolean {
    // Check if this is explicitly marked as non-retryable
    if (error instanceof NonRetryableError) {
      return false
    }

    // All other errors are retryable
    return true
  }

  /**
   * One toast per entry into an actionable error state (terminal failure or signed-out);
   * afterwards the ambient indicators carry the state (sync-feedback.md §4.4). Signed-out users
   * WITH queued changes still get the toast — that's exactly when they need it; only anonymous
   * users with nothing queued stay quiet.
   */
  private showAttentionToastOnce(isAuthIssue: boolean): void {
    if (this.attentionToastShown) {
      return
    }

    // The open dialog already shows the state in place — a toast would be double feedback.
    // Deliberately does NOT mark the toast as shown: if the problem outlives the dialog,
    // the next failure cycle toasts once.
    if (this.syncDialogVisible.value) {
      return
    }

    const suppressed = shouldSuppressSyncErrorNotifications() && !this.hasUnsyncedChanges()
    if (suppressed) {
      return
    }

    const signedOut = isAuthIssue || shouldSuppressSyncErrorNotifications()
    showError(signedOut ? syncToasts.signedOut : syncToasts.needsAttention)
    this.attentionToastShown = true
  }

  /**
   * Handle sync error and determine retry strategy
   */
  private handleSyncError(error: unknown, syncId: string): void {
    const errorMessage = error instanceof Error ? error.message : String(error)

    // Check if this is a retryable error
    if (!this.isRetryableError(error)) {
      // Non-retryable error (e.g. AuthenticationError):
      // Show error, set error state, but DO NOT reset retry state (so error state persists)
      this.showAttentionToastOnce(error instanceof AuthenticationError)
      this.syncStatus.value = 'error'
      this.lastErrorMessage.value = errorMessage
      this.completeSyncOperation(syncId, 'error', errorMessage)
      // Do NOT call this.resetRetryState() here!
      return
    }

    // Retryable error - handle retry logic
    this.syncStatus.value = 'error'
    this.lastErrorMessage.value = errorMessage

    if (this.retryCount >= this.maxRetries) {
      // Max retries reached - start cooldown period
      this.startCooldownPeriod(syncId, errorMessage)
    } else {
      // Schedule retry with exponential backoff
      this.scheduleRetry(syncId, errorMessage)
    }
  }

  /**
   * Schedule a retry attempt with exponential backoff
   */
  private scheduleRetry(syncId: string, errorMessage: string): void {
    const delay = this.calculateRetryDelay()
    const retryMessage = `Retrying in ${delay / 1000}s (attempt ${this.retryCount + 1}/${this.maxRetries})`

    // Update sync tracking with retry message
    this.completeSyncOperation(syncId, 'error', `${errorMessage} - ${retryMessage}`)

    // Surface the schedule to ambient indicators; no error toast during retry attempts
    this.retryAttempt.value = this.retryCount + 1
    this.nextRetryAt.value = Date.now() + delay
    this.retryTimeoutId = window.setTimeout(() => {
      this.retryTimeoutId = null
      this.nextRetryAt.value = null
      this.retryCount++
      this.synchronize()
    }, delay)
  }

  /**
   * Start cooldown period after max retries reached
   */
  private startCooldownPeriod(syncId: string, errorMessage: string): void {
    this.isInCooldown = true
    this.retryCount = 0
    // A full retry cycle failed — the UI stops promising that retrying will fix it.
    this.persistentFailure.value = true

    const cooldownMessage = `Max retries reached. Cooling down for ${this.cooldownPeriod / 1000}s`

    // Show error after max retries — once per error episode, then ambient indicators carry it
    this.showAttentionToastOnce(false)
    this.completeSyncOperation(syncId, 'error', `${errorMessage} - ${cooldownMessage}`)

    this.retryAttempt.value = 0
    this.nextRetryAt.value = null
    this.cooldownUntil.value = Date.now() + this.cooldownPeriod
    this.cooldownTimeoutId = window.setTimeout(() => {
      this.isInCooldown = false
      this.cooldownTimeoutId = null
      this.cooldownUntil.value = null

      // After cooldown, try syncing again if there are unsynced events and we're online
      if (this.hasUnsyncedChanges() && connectivityService.isEffectivelyOnline.value) {
        this.synchronize()
      }
    }, this.cooldownPeriod)
  }

  /**
   * Handle device coming back online - check for unsynced changes and sync if needed
   * This method can be called manually or is automatically triggered by the online status watcher
   */
  async handleOnlineStatusChange(): Promise<void> {
    if (!connectivityService.isEffectivelyOnline.value) {
      this.syncStatus.value = 'offline'
      this.lastErrorMessage.value = connectivityService.forceOfflineMode.value
        ? 'Forced offline mode is enabled'
        : 'Device is offline'
      return
    }

    // Reset status from offline to idle if needed
    if (this.syncStatus.value === 'offline') {
      this.syncStatus.value = 'idle'
    }

    // Reset retry state when coming back online (fresh start)
    this.resetRetryState()

    // Check for unsynced changes and sync if needed
    if (this.hasUnsyncedChanges()) {
      await this.synchronize()
    }
  }

  /**
   * Resolves once the sync status is no longer 'syncing'.
   * If a sync is already in progress, waits for it to finish.
   * If no sync is running, resolves immediately.
   */
  waitForSyncIdle(): Promise<void> {
    if (this.syncStatus.value !== 'syncing') {
      return Promise.resolve()
    }

    return new Promise((resolve) => {
      const stop = watch(this.syncStatus, (status) => {
        if (status !== 'syncing') {
          stop()
          resolve()
        }
      })
    })
  }

  /**
   * Check if there are any unsynchronized changes
   */
  hasUnsyncedChanges(): boolean {
    const eventStore = useEventStore()
    // Quarantined events are unsynced user work too — leaving destructively would lose them.
    return eventStore.unsyncedEvents.length > 0 || eventStore.quarantinedEvents.length > 0
  }

  /**
   * Two different consequences share this guard (sync-feedback.md §9/§10.2):
   * - non-destructive leave (tab change, reload): the queue persists in IndexedDB and resumes,
   *   so the copy says so instead of threatening data loss.
   * - destructive leave (`destructive: true`, e.g. switching/creating a collection, which runs
   *   `resetAllStores` and DELETES the local queue): the copy names the exact count and
   *   consequence so a long-offline queue is never wiped on a misread dialog.
   */
  async confirmSafeToLeave(
    leaveActionLabel: string,
    options?: { destructive?: boolean },
  ): Promise<boolean> {
    if (!this.hasUnsyncedChanges()) {
      return true
    }

    const eventStore = useEventStore()
    // Count quarantined items too — they are unsynced user work and a destructive leave
    // deletes them as well (the guard fires because of them, so the count must include them).
    const unsyncedCount = eventStore.unsyncedEvents.length + eventStore.quarantinedEvents.length
    const { title, message } = leaveGuardCopy(unsyncedCount, options?.destructive ?? false)

    const confirmed = await showConfirm({
      title,
      message,
      confirmText: leaveActionLabel,
      cancelText: 'Check Sync Status',
      variant: 'warning',
    })

    if (confirmed) {
      return true
    }

    window.dispatchEvent(new CustomEvent('open-sync-status'))
    return false
  }

  /**
   * Start tracking a new sync operation
   */
  private startSyncOperation(): string {
    const syncId = generateId()
    const retryInfo = this.retryCount > 0 ? ` (retry ${this.retryCount})` : ''
    logService.info(LOG_CATEGORIES.SYNC, `Sync operation started${retryInfo}`, {
      syncId,
      retryAttempt: this.retryCount,
    })
    return syncId
  }

  /**
   * Complete tracking of a sync operation
   */
  private completeSyncOperation(
    syncId: string,
    status: 'success' | 'error',
    message: string | null,
    syncedData?: { events: number; size: number },
  ) {
    const level = status === 'success' ? 'success' : 'error'
    logService.log(level, LOG_CATEGORIES.SYNC, message || `Sync ${status}`, {
      syncId,
      status,
      syncedEvents: syncedData?.events,
      syncedSize: syncedData?.size,
    })
  }

  /**
   * Get sync logs from the general logging service
   */
  getSyncLogs() {
    return logService.getLogsByCategory(LOG_CATEGORIES.SYNC)
  }

  /**
   * Schedule a sync operation with a delay
   */
  scheduleSync(): void {
    // Don't schedule sync if we're in error state (likely authentication error)
    if (this.syncStatus.value === 'error') {
      return
    }

    if (this.syncSuppressionDepth > 0) {
      this.syncRequestedDuringSuppression = true
      return
    }

    // If sync is already scheduled, don't reset the timer
    // This ensures that when multiple files are processed in batch,
    // sync starts after the first file becomes available
    if (this.syncTimeoutId !== null) {
      return
    }

    // Schedule new sync in 1 second
    this.syncTimeoutId = window.setTimeout(() => {
      this.syncTimeoutId = null
      if (this.syncSuppressionDepth > 0) {
        this.syncRequestedDuringSuppression = true
        return
      }
      if (
        this.hasUnsyncedChanges() &&
        connectivityService.isEffectivelyOnline.value &&
        this.syncStatus.value !== 'error'
      ) {
        void this.synchronize()
      }
    }, CONFIG.AUTO_SYNC_DELAY_MS)
  }

  /**
   * Synchronize all changes with the server
   */
  async synchronize(options?: { force?: boolean }): Promise<boolean> {
    // Check if we're in cooldown period
    if (this.isInCooldown) {
      if (!options?.force) {
        return false
      }

      // Manual sync during cooldown: break the cooldown for this attempt, but keep
      // persistentFailure/lastFailedBatchClientIds — those only clear on actual success,
      // so the honest failure copy never flaps back to optimistic.
      logService.info(LOG_CATEGORIES.SYNC, 'Force sync requested during cooldown', {
        retryAttempt: this.retryCount,
      })
      this.clearRetryTimeouts()
      this.isInCooldown = false
    }

    // A manual "Sync now" during a scheduled retry countdown supersedes it: cancel the pending
    // timer and CONSUME the attempt the timer was counting down to — the user-visible attempt
    // counter advances with the manual try instead of jumping backward. Repeated failing manual
    // attempts therefore also escalate to cooldown/persistent-failure honestly.
    if (options?.force && this.retryTimeoutId !== null) {
      logService.info(LOG_CATEGORIES.SYNC, 'Force sync supersedes the scheduled retry', {
        retryAttempt: this.retryCount,
      })
      this.clearRetryTimeouts()
      this.retryCount++
    }

    // Check if we're online
    if (!connectivityService.isEffectivelyOnline.value) {
      const errorMessage = connectivityService.forceOfflineMode.value
        ? 'Cannot synchronize: forced offline mode is enabled'
        : 'Cannot synchronize: device is offline'
      this.syncStatus.value = 'offline'
      this.lastErrorMessage.value = errorMessage
      return false
    }

    // Check if user is authenticated
    const authStore = useAuthStore()
    if (!authStore.isAuthenticated) {
      const errorMessage = 'Cannot synchronize: user is not logged in'
      this.syncStatus.value = 'idle'
      this.lastErrorMessage.value = errorMessage
      logService.info(LOG_CATEGORIES.SYNC, errorMessage, {
        hasUnsyncedEvents: this.hasUnsyncedChanges(),
        eventCount: useEventStore().unsyncedEvents.length,
      })
      return false
    }
    const ownerUserId = authStore.user?.userId
    if (!ownerUserId) return false

    // Prevent multiple syncs running at the same time
    if (this.syncStatus.value === 'syncing') {
      return false
    }

    let ownerContext: SyncOwnerContext
    try {
      ownerContext = this.createSyncOwnerContext(ownerUserId)
    } catch (error) {
      if (error instanceof EventQueueOwnerChangedError) return false
      throw error
    }

    // Start tracking this sync operation
    const syncId = this.startSyncOperation()

    try {
      this.syncStatus.value = 'syncing'

      // Perform the actual synchronization
      const result = await this._performSynchronization(ownerContext)
      this.assertSyncOwnerCurrent(ownerContext)

      if (result && result.success) {
        this.syncStatus.value = 'success'
        this.lastErrorMessage.value = null

        // Reset retry state on successful sync
        this.resetRetryState()

        // Create detailed success message
        const successMessage =
          result.events > 0
            ? `Synchronized ${result.events} event${result.events !== 1 ? 's' : ''} (${formatByteSize(result.size)})`
            : 'No changes to synchronize'

        this.completeSyncOperation(syncId, 'success', successMessage, {
          events: result.events,
          size: result.size,
        })

        // After successful sync, check if there are still unsynced events
        const eventStore = useEventStore()
        if (eventStore.unsyncedEvents.length > 0 && connectivityService.isEffectivelyOnline.value) {
          // Schedule another sync for remaining events
          this.scheduleSync()
        } else if (eventStore.unsyncedEvents.length === 0) {
          // Drain complete — reset the progress baseline
          this.drainTotal.value = null
        }
      } else {
        // If result is false, something went wrong but no exception was thrown
        const errorMessage = 'Synchronization failed without throwing an error'
        this.handleSyncError(new Error(errorMessage), syncId)
      }

      return result !== false
    } catch (error) {
      if (error instanceof EventQueueOwnerChangedError || !this.isSyncOwnerCurrent(ownerContext)) {
        return false
      }
      // Handle the error using our centralized error handling
      this.handleSyncError(error, syncId)
      return false
    } finally {
      // Only reset to idle if we're currently syncing
      if (this.isSyncOwnerCurrent(ownerContext) && this.syncStatus.value === 'syncing') {
        this.syncStatus.value = 'idle'
      }
      if (this.activeSyncAbortController?.signal === ownerContext.signal) {
        this.activeSyncAbortController = null
      }
    }
  }

  /**
   * Internal method to perform the actual synchronization
   * Separated to improve error handling
   */
  private async _performSynchronization(
    ownerContext: SyncOwnerContext,
  ): Promise<{ success: boolean; events: number; size: number } | false> {
    const eventStore = useEventStore()

    try {
      await eventStore.pruneInvalidLegacyEvents(true)

      // Get all unsynced events
      let unsyncedEvents = eventStore.unsyncedEvents

      // Establish (or grow) the drain baseline for user-facing progress. Math.max keeps the
      // baseline stable when a retry re-enters with a partially drained queue, and grows it
      // when new events were queued mid-drain.
      this.drainTotal.value =
        unsyncedEvents.length > 0
          ? Math.max(this.drainTotal.value ?? 0, unsyncedEvents.length)
          : null

      // Track sync metrics
      let totalSyncedEvents = 0
      let totalSyncedSize = 0
      const syncedEventsList: Event[] = []

      if (unsyncedEvents.length > 0) {
        // Process events in batches until no more unsynced events remain
        while (unsyncedEvents.length > 0) {
          // Create a batch of events that doesn't exceed the size limit
          const batch = this.createBatch(unsyncedEvents)

          if (batch.length === 0) {
            // Safety check - if we can't create a batch, throw error
            throw new Error('Unable to create a batch of events to send')
          }

          try {
            // Log batch processing
            logService.info(LOG_CATEGORIES.SYNC, `Processing batch of ${batch.length} events`, {
              batchSize: batch.length,
              totalEvents: unsyncedEvents.length,
            })

            // Process the batch. With Option C this commits the valid events and quarantines any
            // the server rejected in-line, so it returns the events that actually synced.
            const { committed } = await this.processBatch(batch, ownerContext)

            // Track committed metrics only — quarantined events were NOT synced.
            totalSyncedEvents += committed.length
            totalSyncedSize += committed.reduce((sum, event) => sum + (event._sizeBytes || 0), 0)

            // Store the successfully synced events
            syncedEventsList.push(...committed)

            // Update the last sync timestamp after each successful batch
            await eventStore.updateLastSyncTimestamp(Date.now())

            // Get the updated list of unsynced events
            unsyncedEvents = eventStore.unsyncedEvents

            // Grow the drain baseline if work was queued while this batch was in flight. Without
            // this the total is frozen at the loop's start, so the live queue can exceed it and
            // "X of Y" progress undercounts or jumps backward (sync-feedback.md §4.2).
            this.drainTotal.value = Math.max(
              this.drainTotal.value ?? 0,
              totalSyncedEvents + unsyncedEvents.length,
            )
          } catch (batchError) {
            if (batchError instanceof EventQueueOwnerChangedError) {
              throw batchError
            }
            // Preserve AuthenticationError types - don't wrap them
            if (batchError instanceof AuthenticationError) {
              throw batchError
            }

            // Content rejection: quarantine the rejected events and KEEP SYNCING the rest —
            // one bad change must never hold the whole queue hostage (sync-feedback.md §6).
            if (batchError instanceof ValidationError) {
              await this.quarantineRejectedEvents(
                batch,
                batchError.message,
                batchError.failedEvents,
              )
              unsyncedEvents = eventStore.unsyncedEvents
              continue
            }

            const errorMessage = `Error processing batch: ${batchError instanceof Error ? batchError.message : String(batchError)}`
            throw new Error(errorMessage)
          }
        }
      }

      // Return sync metrics
      return {
        success: true,
        events: totalSyncedEvents,
        size: totalSyncedSize,
      }
    } catch (error) {
      if (error instanceof EventQueueOwnerChangedError) {
        throw error
      }
      // Preserve AuthenticationError types - don't wrap them
      if (error instanceof AuthenticationError) {
        throw error
      }

      const errorMessage = error instanceof Error ? error.message : String(error)
      throw new Error(errorMessage)
    }
  }

  /**
   * Create a batch of events within size limits
   */
  private createBatch(events: Event[]) {
    const batch: Event[] = []
    let currentBatchSize = 0
    // Entity client-ids created by events already placed in this batch.
    const createdInBatch = new Set<string>()

    for (const event of events) {
      // Add event size plus a buffer for FormData overhead
      const eventSize = (event._sizeBytes || 0) + 1024 // Add 1KB overhead per event

      // Keep dependency chains atomic: never start a *new* HTTP request with an event that depends on
      // an entity created earlier in *this* batch. Splitting e.g. ROUTE_POINT_CREATED away from its
      // TRACK_CREATED / TOUR_CREATED would put the dependent in a later request which — if the backend
      // commits requests independently — can be processed before its prerequisite, surfacing as
      // "Tour not found …". Events stay in queue order, so we only ever defer the size-limit cut to the
      // next safe boundary (an event that does not reference anything created in this batch).
      const dependsOnBatch = referencedClientIds(event).some((id) => createdInBatch.has(id))

      // Cut on size OR item count (progress ticks per batch — sync-feedback.md §4.2), but
      // only at a dependency-safe boundary.
      const limitReached =
        currentBatchSize + eventSize > CONFIG.SYNC_MAX_BATCH_SIZE_BYTES ||
        batch.length >= CONFIG.SYNC_MAX_BATCH_EVENT_COUNT

      if (limitReached && batch.length > 0 && !dependsOnBatch) {
        break
      }

      // Add the event to the batch
      batch.push(event)
      currentBatchSize += eventSize

      for (const createdId of createdClientIds(event)) {
        createdInBatch.add(createdId)
      }
    }

    return batch
  }

  /**
   * Process a single batch of events
   */
  private async processBatch(
    events: Event[],
    ownerContext: SyncOwnerContext,
  ): Promise<BatchOutcome> {
    const eventStore = useEventStore()
    const allFiles: Record<string, File> = {}

    // Process each event to collect files and prepare clean payloads
    const cleanEvents = events.map((event, index) => {
      const cleanPayload = { ...event.payload } as Record<string, unknown>

      // Find all File objects in the payload and remove them
      for (const [key, value] of Object.entries(cleanPayload)) {
        const isFileValue =
          value instanceof File ||
          value instanceof Blob ||
          (typeof value === 'object' &&
            value !== null &&
            '_type' in value &&
            (value as { _type: string })._type === 'File')

        if (isFileValue) {
          // Ignore internal-only fields (prefixed with '_') to avoid duplicate uploads like _originalFile
          if (!key.startsWith('_')) {
            // Store the file with the format expected by apiService
            allFiles[`[${index}]_file_${key}`] = value as File
          }

          delete (cleanPayload as Record<string, unknown>)[key]
        }
      }

      // Return clean event with relevant properties only
      return {
        clientId: event.clientId,
        type: event.type,
        timestamp: event.timestamp,
        payload: getRelevantProperties(cleanPayload) as typeof event.payload,
      }
    })

    try {
      // Surface the in-flight batch to progress indicators
      this.currentBatchCount.value = events.length
      this.currentBatchUploadFraction.value = 0
      this.currentBatchUploadedAt.value = null
      this.currentBatchClientIds.value = new Set(events.map((event) => event.clientId))

      await dbService.assertEventOwnerCurrent(ownerContext.eventOwner)
      this.assertSyncOwnerCurrent(ownerContext)

      // Send the batch to the server using apiService
      const result = await apiService.submitEventBatch(cleanEvents as Event[], allFiles, {
        signal: ownerContext.signal,
        onUploadProgress: (loaded, total) => {
          if (!this.isSyncOwnerCurrent(ownerContext)) return
          const fraction = total && total > 0 ? Math.min(1, loaded / total) : 0
          this.currentBatchUploadFraction.value = fraction
          if (fraction >= 1 && this.currentBatchUploadedAt.value === null) {
            this.currentBatchUploadedAt.value = Date.now()
          }
        },
      })
      this.assertSyncOwnerCurrent(ownerContext)

      if (result.success) {
        // Option C partial success: the server commits every valid event and reports the
        // permanently-rejected ones in failed_events. Remove the committed ones and quarantine the
        // rest — never remove a rejected event as if it synced (that would silently lose it).
        const rejected = result.failedEvents ?? []
        const rejectedIds = new Set(rejected.map((failed) => failed.clientId))
        const committed = events.filter((event) => !rejectedIds.has(event.clientId))

        if (committed.length > 0) {
          await eventStore.removeEvents(committed.map((event) => event.clientId))
          // Update the URL if a WORKSPACE_CREATED event committed
          this.updateUrlAfterWorkspaceCreation(committed)
        }

        let quarantinedCount = 0
        if (rejected.length > 0) {
          quarantinedCount = await this.quarantineRejectedEvents(
            events,
            `Server rejected ${rejected.length} event(s)`,
            rejected,
          )
        }

        this.lastFailedBatchClientIds.value = new Set()
        return { committed, quarantinedCount }
      }

      // Handle server-side validation or processing errors
      throw new Error('Server rejected events: Unknown error')
    } catch (error) {
      if (!this.isSyncOwnerCurrent(ownerContext)) {
        throw new EventQueueOwnerChangedError()
      }
      this.lastFailedBatchClientIds.value = new Set(events.map((event) => event.clientId))
      // Check if this is an axios error with authentication/authorization status codes
      if (axios.isAxiosError(error) && error.response) {
        const status = error.response.status

        // Throw specific error types for auth failures
        if (status === 401 || status === 403) {
          const errorMessage = `Authentication failed (${status}): ${error.response.data?.message || 'Unauthorized'}`
          throw new AuthenticationError(errorMessage, status)
        }

        // Content rejections (4xx validation) can never succeed by retrying — quarantine path
        if (VALIDATION_STATUS_CODES.has(status)) {
          const errorMessage = `Server rejected events (${status}): ${error.response.data?.message || 'Validation failed'}`
          throw new ValidationError(errorMessage, status)
        }
      }

      // apiService wraps non-auth HTTP errors into plain Errors but preserves the status and the
      // structured failed_events — classify those the same way and carry the named culprit(s).
      const wrapped = error as { statusCode?: number; failedEvents?: FailedEvent[] }
      if (wrapped.statusCode !== undefined && VALIDATION_STATUS_CODES.has(wrapped.statusCode)) {
        throw new ValidationError(
          error instanceof Error ? error.message : String(error),
          wrapped.statusCode,
          wrapped.failedEvents,
        )
      }

      // For all other errors, throw a generic error that will be retried
      const errorMessage = `Failed to send events: ${error instanceof Error ? error.message : String(error)}`
      throw new Error(errorMessage)
    } finally {
      if (this.isSyncOwnerCurrent(ownerContext)) {
        this.currentBatchCount.value = 0
        this.currentBatchUploadFraction.value = 0
        this.currentBatchUploadedAt.value = null
        this.currentBatchClientIds.value = new Set()
      }
    }
  }

  /**
   * Quarantine the events the server rejected, and return how many were quarantined. With Option C
   * the server commits every valid event and names the permanently-rejected ones in `failed_events`
   * (processBatch has already removed the committed ones), so only the named events and their
   * dependents are quarantined. When the whole request failed with no per-event detail
   * (`failedEvents` absent), the whole batch is quarantined (coarse fallback; a batch is
   * dependency-atomic, so it usually represents one logical action).
   */
  private async quarantineRejectedEvents(
    batch: Event[],
    reason: string,
    failedEvents?: FailedEvent[],
  ): Promise<number> {
    const eventStore = useEventStore()

    // Quarantine exactly the server-named events, or the whole batch when none were named.
    const seedIds = new Set(
      failedEvents && failedEvents.length > 0
        ? failedEvents.map((failed) => failed.clientId)
        : batch.map((event) => event.clientId),
    )

    // Cascade across the whole queue: events referencing entities created by the quarantined
    // ones would fail with "not found" forever if left behind.
    const targetIds = collectDependentEventIds(eventStore.unsyncedEvents, seedIds)
    await eventStore.quarantineEvents([...targetIds], reason)

    // Quarantined events left the queue but were NOT synced — shrink the drain baseline so
    // the progress bar never counts rejections as progress ("8 of 16 synced" would be a lie).
    if (this.drainTotal.value !== null) {
      const reduced = this.drainTotal.value - targetIds.size
      this.drainTotal.value = reduced > 0 ? reduced : null
    }

    logService.warn(LOG_CATEGORIES.SYNC, 'Rejected events quarantined; queue continues', {
      precise: Boolean(failedEvents && failedEvents.length > 0),
      quarantinedCount: targetIds.size,
      batchSize: batch.length,
      reason,
    })

    this.lastFailedBatchClientIds.value = new Set()
    this.showAttentionToastOnce(false)
    return targetIds.size
  }

  /**
   * Update the URL if a WORKSPACE_CREATED event was successfully synced
   */
  private updateUrlAfterWorkspaceCreation(events: Event[]): void {
    // Find WORKSPACE_CREATED events in the batch
    const workspaceCreatedEvent = events.find((event) => event.type === EventType.WORKSPACE_CREATED)

    if (workspaceCreatedEvent) {
      const workspaceId = workspaceCreatedEvent.payload.clientId
      const currentRoute = router.currentRoute.value
      const currentWorkspaceId = currentRoute.params.workspaceId

      // Update URL only when the current workspaceId is the placeholder "workspace".
      // Do NOT push when there is no workspaceId (e.g. library / home route) — that
      // would fire the router.beforeEach guard mid-sync while other events are still pending.
      if (currentWorkspaceId === 'workspace') {
        router.push({
          name: 'workspace-detail',
          params: { workspaceId },
        })
      }
    }
  }

  /**
   * Load workspace data from remote server by client ID
   * This will reset local data and load server data instead
   */
  async loadRemoteWorkspace(clientId: string, skipIfLocal: boolean = false): Promise<boolean> {
    // Get store references
    const workspaceStore = useWorkspaceStore()
    const offlineStore = useOfflineStore()

    // If this clientId refers to a freshly created local workspace (no WORKSPACE_CREATED pushed yet),
    // do not attempt a remote load. This happens right after clicking "+ Workspace".
    if (workspaceStore.clientId === clientId && workspaceStore._createEventPushed === false) {
      return true
    }

    // If skipIfLocal is true and the current workspace is the same as the requested one,
    // don't reload it (this happens when we create a workspace and update the URL)
    if (skipIfLocal && workspaceStore.clientId === clientId) {
      // We're already working with this workspace locally, no need to reload
      return true
    }

    if (!(await this.confirmSafeToLeave('Open Collection', { destructive: true }))) {
      return false
    }

    const userId = useAuthStore().user?.userId ?? null
    const loadEpoch = this.beginWorkspaceLoad()

    try {
      // Reset all stores
      await this.resetAllStores()
      if (!this.isWorkspaceLoadCurrent(loadEpoch, userId)) return false

      // Reset sync state to start fresh with the new collection
      this.resetSyncState()

      if (!connectivityService.isEffectivelyOnline.value) {
        // Native package summaries hydrate after mount so first paint never
        // waits on recovery. Cold offline route loads must await that hydration
        // before deciding whether an API fallback is necessary.
        await offlineStore.init()
        if (!this.isWorkspaceLoadCurrent(loadEpoch, userId)) return false
        if (offlineStore.isOffline(clientId)) {
          const snapshot = await offlineStore.readWorkspaceSnapshot(clientId)
          if (snapshot) {
            if (!this.isWorkspaceLoadCurrent(loadEpoch, userId)) return false
            await this.populateStoresFromRemoteData(snapshot.workspaceDetail, {
              detailsLoaded: true,
            })
            return true
          }
        }
      }

      // Fetch the workspace data from the API
      const workspaceData = await apiService.getWorkspaceDetail(clientId)
      if (!this.isWorkspaceLoadCurrent(loadEpoch, userId)) return false

      // Load the workspace data into the stores
      await this.populateStoresFromRemoteData(workspaceData, { detailsLoaded: false })

      // Workspace detail panel is opened manually via the info button.
      return true
    } catch (error) {
      if (!this.isWorkspaceLoadCurrent(loadEpoch, userId)) return false

      if (isNetworkOfflineError(error)) {
        await offlineStore.init()
        if (!this.isWorkspaceLoadCurrent(loadEpoch, userId)) return false
        if (offlineStore.isOffline(clientId)) {
          const snapshot = await offlineStore.readWorkspaceSnapshot(clientId)
          if (snapshot) {
            if (!this.isWorkspaceLoadCurrent(loadEpoch, userId)) return false
            await this.resetAllStores()
            if (!this.isWorkspaceLoadCurrent(loadEpoch, userId)) return false
            this.resetSyncState()
            await this.populateStoresFromRemoteData(snapshot.workspaceDetail, {
              detailsLoaded: true,
            })
            return true
          }
        }
      }

      const errorMessage = `${error instanceof Error ? error.message : String(error)}`
      await showConfirm({
        title: 'Failed to Load Collection',
        message: errorMessage,
        confirmText: 'OK',
        cancelText: undefined,
        variant: 'danger',
      })
      this.syncStatus.value = 'error'
      this.lastErrorMessage.value = errorMessage
      // Error already logged by this point
      return false
    }
  }

  async loadPinnedWorkspace(): Promise<boolean> {
    const workspaceStore = useWorkspaceStore()

    if (!(await this.confirmSafeToLeave('Open Pinned', { destructive: true }))) {
      return false
    }

    const userId = useAuthStore().user?.userId
    if (!userId) return false
    const loadEpoch = this.beginWorkspaceLoad()

    try {
      await this.resetAllStores()
      if (!this.isWorkspaceLoadCurrent(loadEpoch, userId)) return false
      this.resetSyncState()
      const workspaceData = await apiService.getPinnedWorkspaceDetail()
      if (!this.isWorkspaceLoadCurrent(loadEpoch, userId)) return false
      await this.populateStoresFromRemoteData(workspaceData, { detailsLoaded: false })
      return true
    } catch (error) {
      if (!this.isWorkspaceLoadCurrent(loadEpoch, userId)) return false

      const errorMessage = `${error instanceof Error ? error.message : String(error)}`
      await showConfirm({
        title: 'Failed to Load Pinned',
        message: errorMessage,
        confirmText: 'OK',
        cancelText: undefined,
        variant: 'danger',
      })
      this.syncStatus.value = 'error'
      this.lastErrorMessage.value = errorMessage
      if (workspaceStore.clientId) {
        workspaceStore.clearToOverviewState()
      }
      return false
    }
  }

  async refreshCurrentRemoteWorkspace(clientId: string): Promise<boolean> {
    const workspaceStore = useWorkspaceStore()

    if (this.hasUnsyncedChanges()) {
      return false
    }

    if (workspaceStore.clientId !== clientId) {
      return this.loadRemoteWorkspace(clientId, false)
    }

    if (workspaceStore._createEventPushed === false) {
      return true
    }

    try {
      const workspaceData = await apiService.getWorkspaceDetail(clientId)
      if (workspaceStore.clientId !== clientId) {
        return false
      }

      await this.populateStoresFromRemoteData(workspaceData, {
        detailsLoaded: false,
        preserveCurrentState: true,
      })

      logService.info(LOG_CATEGORIES.SYNC, 'Refreshed current workspace without resetting stores', {
        workspaceId: clientId,
      })
      return true
    } catch (error) {
      logService.warn(LOG_CATEGORIES.SYNC, 'Current workspace refresh failed', {
        workspaceId: clientId,
        error,
      })
      return false
    }
  }

  /**
   * Reset all stores - clearing IndexedDB and state
   * This method is exposed for use with the "New Workspace" functionality
   */
  async resetAllStores(): Promise<void> {
    // Get store references
    const eventStore = useEventStore()
    const workspaceStore = useWorkspaceStore()
    const photoStore = usePhotoStore()

    // Clear only the active owner's durable event queue; preserve its ownership marker.
    await eventStore.clearOwnedEvents()

    // Reset Pinia stores
    workspaceStore.$reset()
    photoStore.$reset()

    // Reset sync state when resetting all stores
    this.resetSyncState()
  }

  /**
   * Populate stores with data from remote server
   */
  private async populateStoresFromRemoteData(
    workspaceData: WorkspaceDetailResponse,
    options: PopulateStoresOptions,
  ): Promise<void> {
    const workspaceStore = useWorkspaceStore()
    const photoStore = usePhotoStore()
    const previousWorkspaceLoading = workspaceStore._isLoading
    const previousPhotoLoading = photoStore.isLoading
    const preserveCurrentState = options.preserveCurrentState === true
    const existingToursById = preserveCurrentState
      ? new Map(workspaceStore.tours.map((tour) => [tour.clientId, tour]))
      : new Map<string, Tour>()

    workspaceStore._isLoading = true
    photoStore.isLoading = true

    try {
      await this.applyRemoteWorkspaceData(workspaceData, {
        detailsLoaded: options.detailsLoaded,
        existingToursById,
      })

      workspaceStore.updateSnapshotAfterSync()
      workspaceStore.updateTourSnapshotsAfterSync()
      workspaceStore.updateTrackSnapshotsAfterSync()
      workspaceStore.updateWaypointSnapshotsAfterSync()
      workspaceStore.updateWorkspacePOISnapshotsAfterSync()
    } finally {
      workspaceStore._isLoading = previousWorkspaceLoading
      photoStore.isLoading = previousPhotoLoading
    }
  }

  private async applyRemoteWorkspaceData(
    workspaceData: WorkspaceDetailResponse,
    options: { detailsLoaded: boolean; existingToursById: Map<string, Tour> },
  ): Promise<void> {
    const workspaceStore = useWorkspaceStore()
    const photoStore = usePhotoStore()

    // Set workspace data
    workspaceStore.clientId = workspaceData.clientId
    workspaceStore.name = workspaceData.name
    workspaceStore.description = workspaceData.description || null
    workspaceStore.isPublic = workspaceData.isPublic || false
    workspaceStore.systemKey = workspaceData.systemKey ?? null
    workspaceStore.isSystem = workspaceData.isSystem === true
    workspaceStore.markedForDeletion = workspaceData.markedForDeletion || false
    workspaceStore.deletionScheduledAt = workspaceData.deletionScheduledAt || null
    workspaceStore._createEventPushed = true

    // Set user role and permission fields from API
    workspaceStore.userRole = workspaceData.userRole || null
    workspaceStore.canEdit = workspaceData.canEdit || null
    workspaceStore.canManage = workspaceData.canManage ?? null
    workspaceStore.canView = workspaceData.canView || null
    workspaceStore.isBookmarked = workspaceData.isBookmarked || false
    workspaceStore.ownerNames = workspaceData.ownerNames || null

    // Set cover photo fields from API
    workspaceStore.coverPhotoClientId = workspaceData.coverPhotoClientId ?? null
    workspaceStore.coverPhotoThumbnailUrl =
      resolveApiResourceUrl(workspaceData.coverPhotoThumbnailUrl) ?? null
    workspaceStore.coverPhotoUrl = resolveApiResourceUrl(workspaceData.coverPhotoUrl) ?? null

    workspaceStore.pois = (workspaceData.pois ?? []).map((remotePOI) => {
      const poi: WorkspacePOI = {
        ...remotePOI,
        clientId: remotePOI.clientId || generateId(),
        workspaceClientId: remotePOI.workspaceClientId || workspaceData.clientId,
        sourceWorkspaceClientId:
          remotePOI.sourceWorkspaceClientId ||
          remotePOI.workspaceClientId ||
          workspaceData.clientId,
        description: remotePOI.description,
        _previousState: undefined,
      }
      poi._previousState = poiSnapshot(poi)
      return poi
    })

    // Process tours with proper default visibility values
    const processedTours = workspaceData.tours.map((tour) => ({
      ...tour,
      visible:
        tour.visible !== undefined
          ? tour.visible
          : (options.existingToursById.get(tour.clientId)?.visible ?? true),
      active:
        tour.active !== undefined
          ? tour.active
          : (options.existingToursById.get(tour.clientId)?.active ?? false),
      expanded:
        tour.expanded !== undefined
          ? tour.expanded
          : options.existingToursById.get(tour.clientId)?.expanded,
      _detailsLoaded: options.detailsLoaded,
      tracks: tour.tracks.map((track) => ({
        ...track,
        visible:
          track.visible !== undefined
            ? track.visible
            : (options.existingToursById
                .get(tour.clientId)
                ?.tracks.find((candidate) => candidate.clientId === track.clientId)?.visible ??
              true),
        segments: hydrateApiSegments(track.segments),
      })),
      waypoints: (tour.waypoints ?? []).map((rawWaypoint) => {
        const waypoint = rawWaypoint as typeof rawWaypoint & { desc?: string }
        let timeValue: number | undefined = undefined
        if (waypoint.time) {
          const timeAsNumber = Number(waypoint.time)
          timeValue = isNaN(timeAsNumber) ? new Date(waypoint.time).getTime() : timeAsNumber
        }

        return {
          ...waypoint,
          clientId: waypoint.clientId || generateId(),
          description: waypoint.description ?? waypoint.desc,
          time: timeValue,
          _previousState: undefined,
        }
      }),
      routePoints: (tour.routePoints ?? []).map((rawRoutePoint, index) => {
        const routePoint = rawRoutePoint as typeof rawRoutePoint & { desc?: string }
        return {
          ...routePoint,
          clientId: routePoint.clientId || generateId(),
          description: routePoint.description ?? routePoint.desc,
          order: routePoint.order ?? index,
          trackClientId: requireTrackClientId(routePoint.trackClientId, 'Synced route point'),
          _previousState: undefined,
        }
      }),
    })) as Tour[]
    applyPersistedWorkspaceTourVisibility(workspaceData.clientId, processedTours)

    // Set tours with processed visibility defaults
    workspaceStore.tours = processedTours
    const processedTourIds = new Set(processedTours.map((tour) => tour.clientId))
    if (workspaceStore.selectedTourId && !processedTourIds.has(workspaceStore.selectedTourId)) {
      workspaceStore.selectTour(null)
    }

    // Process tours - no persistence, data stays in Pinia store only
    // Tours are set directly to workspaceStore.tours above

    // Workspace data is already set in workspaceStore above - no persistence needed

    // Process photos
    const processedPhotos: Photo[] = []
    for (const remotePhoto of workspaceData.photos) {
      try {
        if (remotePhoto.url) {
          const resolvedPhotoUrl = resolveApiResourceUrl(remotePhoto.url)
          const resolvedThumbnailUrl = resolveApiResourceUrl(remotePhoto.thumbnailUrl)

          const photo: Photo = {
            clientId: remotePhoto.clientId,
            name: remotePhoto.name || 'Untitled Photo',
            _binaryDataUrl: resolvedPhotoUrl || remotePhoto.url,
            _thumbnailUrl: resolvedThumbnailUrl ?? remotePhoto.thumbnailUrl ?? null,
            hasCoordinates: remotePhoto.hasCoordinates,
            lat: remotePhoto.lat,
            lon: remotePhoto.lon,
            takenAt: remotePhoto.takenAt,
            uploadedByName: remotePhoto.uploadedByName || null,
            canEdit: remotePhoto.canEdit ?? null,
          }

          processedPhotos.push(photo)
        }
      } catch (error) {
        const errorMessage = `Failed to process photo ${remotePhoto.name || 'unknown'}: ${error instanceof Error ? error.message : String(error)}`
        if (!shouldSuppressSyncErrorNotifications()) {
          showError(errorMessage)
        }
        this.syncStatus.value = 'error'
        this.lastErrorMessage.value = errorMessage
        // Error already logged by this point
      }
    }
    photoStore.photos = processedPhotos
    const processedPhotoIds = new Set(processedPhotos.map((photo) => photo.clientId))
    if (photoStore.selectedPhotoId && !processedPhotoIds.has(photoStore.selectedPhotoId)) {
      photoStore.selectedPhotoId = null
    }
  }

  /**
   * Handle authentication recovery (e.g., after user logs in following 401/403 error)
   * This method should be called when the user successfully authenticates
   */
  async handleAuthenticationRecovery(): Promise<void> {
    // Reset retry state to allow fresh sync attempts
    this.resetRetryState()

    // Reset error state if it was an authentication error
    if (this.syncStatus.value === 'error') {
      this.syncStatus.value = 'idle'
      this.lastErrorMessage.value = null
    }

    // Check for unsynced changes and sync if needed
    if (this.hasUnsyncedChanges() && connectivityService.isEffectivelyOnline.value) {
      await this.synchronize()
    }
  }

  /**
   * Reset sync state (status, error messages, retry state, cooldown)
   * Useful when loading a new collection to start with a clean sync state
   */
  resetSyncState(): void {
    // Clear any pending timeouts
    this.clearRetryTimeouts()
    if (this.syncTimeoutId !== null) {
      clearTimeout(this.syncTimeoutId)
      this.syncTimeoutId = null
    }

    // Reset all sync state
    this.syncStatus.value = 'idle'
    this.lastErrorMessage.value = null
    this.retryCount = 0
    this.isInCooldown = false
    this.drainTotal.value = null

    // A "clean slate" must also drop the failure-episode state, otherwise switching/loading a
    // collection after a terminal failure carries persistent-failure copy and a suppressed
    // attention toast into the next, unrelated sync issue.
    this.persistentFailure.value = false
    this.lastFailedBatchClientIds.value = new Set()
    this.attentionToastShown = false
  }

  /**
   * Check if sync service is currently in cooldown period
   */
  get isInCooldownPeriod(): boolean {
    return this.isInCooldown
  }

  /**
   * Get current retry attempt information
   */
  get retryInfo() {
    return {
      currentAttempt: this.retryCount,
      maxRetries: this.maxRetries,
      isInCooldown: this.isInCooldown,
      nextRetryDelay: this.retryTimeoutId ? this.calculateRetryDelay() : null,
    }
  }
}

export const syncService = new SyncService()
